/**
 * ContinuityDocumentsSection — sits inside the ContinuityAssetEditor modal.
 *
 * Lets the user attach up to 3 documents per asset. Handles listing,
 * uploading (via multipart POST), downloading (via GET → blob), and
 * deletion. All copy is plain-English so the UI is friendly for
 * non-technical users.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Paperclip, Upload, Trash2, Download, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_EXT = '.pdf,.jpg,.jpeg,.png,.heic,.doc,.docx,.xls,.xlsx,.csv';
const MAX_FILES = 3;
const MAX_BYTES = 10 * 1024 * 1024;

const authHeaders = () => {
  const t = localStorage.getItem('auth_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtSize = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');

async function apiList(investmentId) {
  const r = await fetch(`${BACKEND_URL}/api/investments/${investmentId}/continuity-documents`, { headers: authHeaders() });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Could not load documents.');
  return r.json();
}

async function apiUpload(investmentId, file) {
  const fd = new FormData();
  fd.append('file', file);
  const r = await fetch(`${BACKEND_URL}/api/investments/${investmentId}/continuity-documents`, {
    method: 'POST', headers: authHeaders(), body: fd,
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Upload failed.');
  return r.json();
}

async function apiDelete(investmentId, docId) {
  const r = await fetch(`${BACKEND_URL}/api/investments/${investmentId}/continuity-documents/${docId}`, {
    method: 'DELETE', headers: authHeaders(),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Delete failed.');
  return r.json();
}

async function apiDownload(investmentId, doc) {
  const r = await fetch(
    `${BACKEND_URL}/api/investments/${investmentId}/continuity-documents/${doc.doc_id}/download`,
    { headers: authHeaders() },
  );
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Download failed.');
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = doc.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function ContinuityDocumentsSection({ investmentId, onCountChange }) {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { documents } = await apiList(investmentId);
      setDocs(documents || []);
      onCountChange && onCountChange(documents?.length || 0);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [investmentId, onCountChange]);

  useEffect(() => { if (investmentId) load(); }, [investmentId, load]);

  const handlePick = () => { if (!busy && docs.length < MAX_FILES) inputRef.current?.click(); };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_BYTES) { toast.error('That file is over 10MB. Please choose a smaller one.'); return; }
    setBusy(true);
    try {
      await apiUpload(investmentId, file);
      toast.success('Document uploaded.');
      await load();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Remove "${doc.filename}"?`)) return;
    setBusy(true);
    try {
      await apiDelete(investmentId, doc.doc_id);
      toast.success('Removed.');
      await load();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-t border-slate-100 pt-3 mt-1" data-testid="cd-section">
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
          <Paperclip className="w-3.5 h-3.5 text-slate-500" /> Documents ({docs.length} of {MAX_FILES})
        </div>
        <Button
          size="sm" variant="outline"
          disabled={busy || docs.length >= MAX_FILES}
          onClick={handlePick}
          data-testid="cd-upload-btn"
        >
          <Upload className="w-3.5 h-3.5 mr-1.5" />
          {busy ? 'Uploading…' : (docs.length >= MAX_FILES ? 'Limit reached' : 'Upload a file')}
        </Button>
        <input
          ref={inputRef} type="file" className="hidden"
          accept={ALLOWED_EXT} onChange={handleFile}
          data-testid="cd-file-input"
        />
      </div>
      <p className="text-xs text-slate-500 mb-2">
        Optional. Up to 3 files, 10MB each. Common formats: PDF, JPG, PNG, HEIC, DOC, DOCX, XLS, XLSX, CSV. Files are virus-scanned then encrypted at rest &mdash; only you can read them until the check-in cascade completes.
      </p>
      {loading ? (
        <p className="text-xs text-slate-400">Loading documents…</p>
      ) : docs.length === 0 ? (
        <p className="text-xs text-slate-400 italic">No documents attached yet.</p>
      ) : (
        <ul className="space-y-1.5" data-testid="cd-list">
          {docs.map((d) => (
            <li
              key={d.doc_id}
              className="flex items-center justify-between gap-2 border border-slate-200 rounded px-2 py-1.5 bg-white text-xs"
              data-testid={`cd-row-${d.doc_id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                <div className="min-w-0">
                  <div className="text-slate-800 truncate">{d.filename}</div>
                  <div className="text-slate-400">{fmtSize(d.size_bytes)} &middot; uploaded {fmtDate(d.uploaded_at)}</div>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => apiDownload(investmentId, d).catch((err) => toast.error(err.message))} title="Download" data-testid={`cd-download-${d.doc_id}`}>
                  <Download className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => handleDelete(d)} title="Remove" data-testid={`cd-delete-${d.doc_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
